/**
 * BulkScanOnboard.jsx — extracted from Inventory.jsx (iter-121 refactor).
 * Continuous scan-&-create flow for onboarding catalogues fast: each
 * successful scan POSTs a draft SKU named "New SKU – {code}". Scanner
 * stays open between scans for hands-free flow; duplicates (409) toast
 * a warning instead of crashing.
 *
 * Props:
 *  - onboardedList     []     running session list (used for the title counter)
 *  - onOnboarded       (sku)  callback fired with the created SKU
 *  - onClose           ()     close the whole onboarding overlay
 */
import React, { useState } from 'react';
import BarcodeScanner from './BarcodeScanner';
import { createSku } from '../lib/api';

export default function BulkScanOnboard({ onboardedList, onOnboarded, onClose }) {
  const [scanning, setScanning] = useState(true);
  const [busy, setBusy] = useState(false);
  const [lastStatus, setLastStatus] = useState(null); // {type: 'ok'|'err'|'dup', message}

  const handleScan = async (code) => {
    if (busy) return;
    setBusy(true);
    setLastStatus(null);
    try {
      const name = `New SKU – ${code}`;
      const { data } = await createSku({ name, barcode: code });
      onOnboarded({ id: data.id, sku_code: data.sku_code, barcode: data.barcode, name: data.name });
      setLastStatus({ type: 'ok', message: `✓ ${data.sku_code} added` });
    } catch (e) {
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail || 'Failed';
      if (status === 409) {
        setLastStatus({ type: 'dup', message: `⚠ ${detail}` });
      } else {
        setLastStatus({ type: 'err', message: `✗ ${detail}` });
      }
    } finally {
      setBusy(false);
      // Hold the feedback for ~1s then resume scanning
      setTimeout(() => {
        setLastStatus(null);
        setScanning(true);
      }, 1000);
    }
  };

  return (
    <>
      {scanning && (
        <BarcodeScanner
          title={`Scan & Onboard · ${onboardedList.length} added`}
          onScan={(code) => {
            setScanning(false);
            handleScan(code);
          }}
          onClose={onClose}
        />
      )}
      {/* After each scan, the scanner unmounts briefly so its internal state resets
          — then we re-mount it via setScanning(true) in the timeout above. Between
          mounts, show a tiny status toast-style overlay so the operator has feedback. */}
      {!scanning && lastStatus && (
        <div
          className="fixed inset-0 z-[70] bg-black/95 flex flex-col items-center justify-center"
          data-testid="bulk-scan-feedback"
        >
          <div className={`text-5xl font-bold mb-4 ${
            lastStatus.type === 'ok' ? 'text-emerald-400' :
            lastStatus.type === 'dup' ? 'text-amber-400' : 'text-rose-400'
          }`}>
            {lastStatus.type === 'ok' ? '✓' : lastStatus.type === 'dup' ? '!' : '✗'}
          </div>
          <p className="text-white text-lg font-mono">{lastStatus.message}</p>
          <p className="text-white/60 text-sm mt-4">Returning to scanner…</p>
        </div>
      )}
    </>
  );
}
